import React from 'react'
import { Gamepad2, Check, X } from 'lucide-react'
import type { PlatformSelectionProps, GamingPlatform, GamingPlatformConfig } from '@/features/enrollment'
import GamingPlatformCard from './GamingPlatformCard'

export default function PlatformSelection({
  enrollmentData,
  updateEnrollmentData,
  updatePlatformData,
  onCheckGamerTag,
  loadingStates,
  gamerTagResults,
  canProceed,
  getUnverifiedPlatforms,
  onReportProblem,
  reportedProblems = {}
}: PlatformSelectionProps) {

  const platformConfigs: Record<GamingPlatform, GamingPlatformConfig> = {
    xbox: {
      name: 'XBOX',
      color: 'bg-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      hoverBorderColor: 'hover:border-green-300',
      selectedBgColor: 'bg-green-50 border-green-300',
      selectedBorderColor: 'border-green-300',
      workflow: [
        '• Open XBOX App webhook',
        '• Search & find GamerTag',
        '• Copy GamerTag to system',
        '• Verify trophy settings'
      ]
    },
    playstation: {
      name: 'PlayStation',
      color: 'bg-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverBorderColor: 'hover:border-blue-300',
      selectedBgColor: 'bg-blue-50 border-blue-300',
      selectedBorderColor: 'border-blue-300',
      workflow: [
        '• Open PlayStation App',
        '• Search & find GamerTag',
        '• Copy GamerTag to system',
        '• Verify trophy settings'
      ]
    },
    steam: {
      name: 'STEAM',
      color: 'bg-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      hoverBorderColor: 'hover:border-purple-300',
      selectedBgColor: 'bg-purple-50 border-purple-300',
      selectedBorderColor: 'border-purple-300',
      workflow: [
        '• Open STEAM ID lookup',
        '• Search & find GamerTag',
        '• Copy GamerTag to system',
        '• Check public settings'
      ]
    }
  }

  const handleSkipGamerTags = () => {
    
    // Clear any existing gamer tag data when skipping
    const platforms: GamingPlatform[] = ['xbox', 'playstation', 'steam']
    platforms.forEach(platform => {
      updatePlatformData(platform, 'gamerTag', '')
      updatePlatformData(platform, 'gamerTagCaptured', false)
      updatePlatformData(platform, 'verificationResult', null)
    })
    // Clear selected platforms
    updateEnrollmentData('selectedPlatforms', [])
    updateEnrollmentData('selectedPlatform', '')
    // Clear intake gamer tags from Step 2
    updateEnrollmentData('intakeXboxGamerTag', '')
    updateEnrollmentData('intakePlaystationGamerTag', '')
    updateEnrollmentData('intakeSteamGamerTag', '')
    // Clear Step 2 platforms data
    updateEnrollmentData('platforms', {
      xbox: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' },
      playstation: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' },
      steam: { gamerTagCaptured: false, trophySettingPublic: false, gamerTag: '' }
    })
    // Set skip flag
    updateEnrollmentData('gamerTagsSkipped', true)
   
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
          <Gamepad2 className="w-6 h-6 text-secondary-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-accent-900">Gaming Platform Selection</h3>
          <p className="text-sm text-accent-600">Select the gaming platform to verify for this client</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
        {Object.entries(platformConfigs).map(([platform, config]) => (
          <GamingPlatformCard
            key={platform}
            platform={platform as GamingPlatform}
            config={config}
            isSelected={enrollmentData.selectedPlatforms?.includes(platform)}
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            updatePlatformData={updatePlatformData}
            onCheckGamerTag={onCheckGamerTag}
            loadingStates={loadingStates}
            gamerTagResults={gamerTagResults}
            onReportProblem={onReportProblem}
            reportedProblems={reportedProblems}
          />
        ))}
      </div>

      {/* No platforms selected message */}
      {(!enrollmentData.selectedPlatforms || enrollmentData.selectedPlatforms.length === 0) && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-center">
          <Gamepad2 className="w-8 h-8 text-primary-400 mx-auto mb-2" />
          <p className="text-primary-700 font-medium">Select gaming platforms to continue</p>
          <p className="text-sm text-primary-600">Click on one or more platforms above to verify the client's gaming accounts</p>
          
          <div className="mt-4 pt-4 border-t border-primary-200">
            <button
              onClick={handleSkipGamerTags}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
            >
              Skip Gamer Tags for Now
            </button>
            <p className="text-xs text-primary-600 mt-2">
              You can select platforms and verify gamer tags later when the client provides them.
            </p>
          </div>
        </div>
      )}

      {/* Verification Status Warning */}
      {enrollmentData.selectedPlatforms && enrollmentData.selectedPlatforms.length > 0 && !canProceed() && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <X className="w-5 h-5 text-red-500" />
            <h4 className="font-medium text-red-800">Verification Required</h4>
          </div>
          <p className="text-red-700 mb-2">
            All selected platforms must have verified gamer tags before continuing.
          </p>
          <div className="text-sm text-red-600">
            <p className="font-medium mb-1">Unverified platforms:</p>
            <ul className="list-disc list-inside space-y-1">
              {getUnverifiedPlatforms().map((platform: string) => (
                <li key={platform} className="capitalize">
                  {platform} - {enrollmentData.platforms?.[platform]?.gamerTag ? 'Verification failed or pending' : 'Enter gamer tag and verify'}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Success Status - Only show when NOT skipped and all platforms verified */}
      {enrollmentData.selectedPlatforms && enrollmentData.selectedPlatforms.length > 0 && canProceed() && !enrollmentData.gamerTagsSkipped && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Check className="w-5 h-5 text-green-500" />
            <h4 className="font-medium text-green-800">All Platforms Verified</h4>
          </div>
          <p className="text-green-700 text-sm mt-1">
            All selected gaming platforms have been successfully verified. You can now continue to the next step.
          </p>
        </div>
      )}

      {/* Skipped Status - Only show when skipped and no platforms verified */}
      {enrollmentData.gamerTagsSkipped && (!enrollmentData.selectedPlatforms || enrollmentData.selectedPlatforms.length === 0 || !canProceed()) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <span className="text-yellow-600 text-lg">⚠️</span>
            <h4 className="font-medium text-yellow-800">Gamer Tags Skipped</h4>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Gamer tag collection has been skipped for now. You can upload gamer tags later when the client provides them.
          </p>
        </div>
      )}
    </div>
  )
}
